import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
	Sliders,
	File,
	ArrowRightCircle,
	ArrowLeftCircle,
	FilePlus,
	Clipboard,
} from 'react-feather';
import {
	fetchMaterials,
	fetchMaterialById,
	fetchCategories,
} from '../slices/materialsSlice';
import Header from '../components/Header';
import Sidebar from '../components/Sidebar';
import InventoryDetails from '../components/inventory/InventoryDetails';
import NewInventory from '../components/inventory/NewInventory';
import BncLogoSpinner from '../components/BncLogoSpinner';

export default function Inventory() {
	const dispatch = useDispatch();
	const material = useSelector((state) => state?.materials?.items) || [];
	const listStatus = useSelector((state) => state?.materials?.listStatus);
	const [newMaterialForm, setNewMaterialForm] = useState(false);
	const token = useSelector((state) => state.auth.token);

	const [materialId, setMaterialId] = useState(null);

	const openDetails = (materialId) => {
		setMaterialId(materialId);
		dispatch(fetchMaterialById(materialId));
	};

	const closeDetails = () => {
		setNewMaterialForm(null);
		if (token) dispatch(fetchMaterials({ pageSize, page: currentPage }));
	};

	const [currentPage, setCurrentPage] = useState(1);
	const [pageSize, setPageSize] = useState(1000);
	const [categoryList, setCategoryList] = useState([]);

	useEffect(() => {
		dispatch(fetchMaterials({ pageSize, page: currentPage }));
	}, [dispatch, pageSize, currentPage]);

	useEffect(() => {
		// fetchCategories and setCategoryList
		dispatch(fetchCategories()).then((action) => {
			if (fetchCategories.fulfilled.match(action)) {
				setCategoryList(action.payload);
			}
		});
	}, [dispatch]);

	useEffect(() => {}, [dispatch, pageSize]);

	// Pagination Handlers
	const handleNextPage = () => {
		setCurrentPage(currentPage + 1);
	};

	const handlePreviousPage = () => {
		if (currentPage > 1) setCurrentPage(currentPage - 1);
	};

	const handlePageSizeChange = (event) => {
		setPageSize(parseInt(event.target.value, 10));
		setCurrentPage(1); // Reset to first page with new page size
	};

	// Filter components
	const [filterName, setFilterName] = useState('');
	const [filterDescription, setFilterDescription] = useState('');
	const [filterCategoryId, setFilterCategoryId] = useState('');
	const [filterWidth, setFilterWidth] = useState('');
	const [filterLength, setFilterLength] = useState('');
	const [filterHeight, setFilterHeight] = useState('');
	const [filterColor, setFilterColor] = useState('');

	const applyFilters = () => {
		dispatch(
			fetchMaterials({
				categoryId: filterCategoryId,
				pageSize: pageSize,
				page: currentPage,
				sortOrder: 'ASC',
				name: filterName,
				width: filterWidth,
				length: filterLength,
				height: filterHeight,
				description: filterDescription,
			})
		);
	};

	const resetFilters = () => {
		setFilterName('');
		setFilterDescription('');
		setFilterCategoryId('');
		setFilterWidth('');
		setFilterLength('');
		setFilterHeight('');
		setFilterColor('');
		applyFilters(); // Fetch all materials without filters
	};

	// Toggle sections
	const [filterSection, setFilterSections] = useState({
		filterOptions: true,
		exportMaterials: false,
		importMaterials: false,
	});

	const toggleSection = (filterSection) => {
		setFilterSections((prevSections) => ({
			...prevSections,
			[filterSection]: !prevSections[filterSection],
		}));
	};

	return (
		<div className="flex flex-col h-screen inventory-page">
			<Header />
			<div className="flex flex-1 overflow-hidden">
				<Sidebar />
				<main className="flex-1 overflow-auto modern-dashboard">
					<div className="content-header">
						<h1>
							<Clipboard size={34} /> Inventory Management 
							<span className="text-sm ml-4 font-normal text-slate-500 bg-slate-100 px-2 py-1 rounded-full border border-slate-200">
								{material?.filter((mate) => (mate?.inventory === true || mate?.inventory === 'true') && mate?.active).length || 0} items
							</span>
						</h1>
						<div className="flex gap-3">
							<button 
								className="quick-access-btn"
								onClick={() => toggleSection('filterOptions')}
							>
								<Sliders size={20} /> Filter
							</button>
							<button 
								className="quick-access-btn primary"
								onClick={() => setNewMaterialForm(true)}
							>
								<FilePlus size={20} /> Add new Item
							</button>
						</div>
					</div>

					<div className="table-card">
						<div className="table-header flex items-center justify-between">
							<h3>Inventory Items</h3>
                            {listStatus === 'loading' && <BncLogoSpinner size={40} />}
						</div>

						{filterSection.filterOptions && (
							<div className="filters-grid">
								<input
									type="text"
									placeholder="Filter by name..."
									value={filterName}
									onChange={(e) => setFilterName(e.target.value)}
								/>
								<input
									type="text"
									placeholder="Filter by description..."
									value={filterDescription}
									onChange={(e) => setFilterDescription(e.target.value)}
								/>
								<input
									type="number"
									placeholder="Width..."
									value={filterWidth}
									onChange={(e) => setFilterWidth(e.target.value)}
								/>
								<input
									type="number"
									placeholder="Length..."
									value={filterLength}
									onChange={(e) => setFilterLength(e.target.value)}
								/>
								<input
									type="number"
									placeholder="Height..."
									value={filterHeight}
									onChange={(e) => setFilterHeight(e.target.value)}
								/>
								<select
									value={filterCategoryId}
									onChange={(e) => setFilterCategoryId(e.target.value)}
								>
									<option value="">Select a Category</option>
									{categoryList?.items?.map((categoryFilter) => (
										<option key={categoryFilter?.id} value={categoryFilter?.id}>
											{categoryFilter?.name}
										</option>
									))}
								</select>
								<input
									type="text"
									placeholder="Filter by color..."
									value={filterColor}
									onChange={(e) => setFilterColor(e.target.value)}
								/>
								<div className="filter-btn-group">
									<button onClick={applyFilters} className="search">
										Apply
									</button>
									<button onClick={resetFilters} className="reset">
										Reset
									</button>
								</div>
							</div>
						)}

						<table className="modern-table">
							<thead>
								<tr>
									<th>Product</th>
									<th>Category</th>
									<th>Color</th>
									<th>Specs</th>
									<th>Stock Status</th>
									<th>Unit Price</th>
									<th className="text-right">Actions</th>
								</tr>
							</thead>
							<tbody>
								{material
									?.slice()
									.reverse()
									.filter((mate) => (mate?.inventory === true || mate?.inventory === 'true') && mate?.active)
									.filter((mate) => {
										if (!filterColor) return true;
										const color = (mate?.metadata?.color || mate?.Metadata?.color || '').toLowerCase();
										return color.includes(filterColor.toLowerCase());
									})
									.map((mate) => (
										<tr key={mate?.id} onClick={() => openDetails(mate?.id)}>
											<td>
												<div className="font-bold text-slate-800">
													{mate?.name}
												</div>
												<div className="text-xs text-slate-500 truncate max-w-[200px]">
													{mate?.description || 'No description'}
												</div>
											</td>
											<td>
												<span className="text-sm font-medium text-slate-600 bg-slate-100 px-2 py-1 rounded-md">
													{mate?.categoryName || 'Uncategorized'}
												</span>
											</td>
											<td>
												<span className="text-sm text-slate-600 font-medium">
													{mate?.metadata?.color || mate?.Metadata?.color || '—'}
												</span>
											</td>
											<td>
												<div className="text-sm text-slate-600">
													{mate?.width ? <>{mate?.width} </> : ''}
													{mate?.length ? <>x {mate?.length}</> : ''}
													{mate?.height ? <> x {mate?.height}</> : ''}
													<span className="ml-1 text-xs text-slate-400 font-bold uppercase">{mate?.measurementUnit}</span>
												</div>
											</td>
											<td>
												{mate?.stockLimit > 10 ? (
													<span className="status-pill status-active">
														{mate?.stockLimit} in stock
													</span>
												) : (
													<span className="status-pill status-blocked">
														Low Stock: {mate?.stockLimit}
													</span>
												)}
											</td>
											<td>
												<strong className="text-slate-900">
													$ {(mate?.unitPrice || 0).toFixed(2)}
												</strong>
											</td>
											<td className="text-right">
												<button className="p-2 text-slate-400 hover:text-blue-600 transition-colors">
													<File size={20} />
												</button>
											</td>
										</tr>
									))}
							</tbody>
						</table>

						<div className="pagination-modern">
							<div className="pagination-btns">
								<button
									onClick={handlePreviousPage}
									disabled={currentPage === 1}
								>
									<ArrowLeftCircle size={20} /> Previous
								</button>
								<strong className="text-slate-800">{currentPage}</strong>
								<button onClick={handleNextPage}>
									Next <ArrowRightCircle size={20} />
								</button>
							</div>
							<div className="flex items-center gap-3">
								<label className="text-sm text-slate-500 font-medium whitespace-nowrap">Items per page:</label>
								<select 
									className="px-3 py-1.5 rounded-lg border border-slate-200 text-sm focus:border-blue-500 outline-none"
									value={pageSize} 
									onChange={handlePageSizeChange}
								>
									<option value="10">10</option>
									<option value="50">50</option>
									<option value="100">100</option>
									<option value="500">500</option>
									<option value="1000">1000</option>
								</select>
							</div>
						</div>
					</div>
						{materialId && (
							<InventoryDetails
								onClose={() => setMaterialId(null)}
								materialId={materialId}
							/>
						)}
						{newMaterialForm && <NewInventory onClose={() => closeDetails()} />}
				</main>
			</div>
		</div>
	);
}
